import { populateBasketTranslationFields } from '@/components/page-specific/catering/common/helpers/translations'
import { BasketErrors } from '@/components/page-specific/catering/common/types/enums/Errors.enum'
import { BasketFragment, GmMenuStandingOrderDataFragment, Language } from '@/generated/graphql'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useCounter } from 'react-use'

import { useBasketSelectionState } from '../../store/basketSelectionStore'
import { useBasketStateStore } from '../../store/basketStateStore'
import { AddItemToBasket, useAddItemMutation } from '../menu-page/add-item-mutation'
import { useGetBasketQuery } from '../menu-page/get-basket-query'
import { useGetStandingOrderQuery } from '../menu-page/get-standing-order-query'
import { useRemoveItemMutation } from '../menu-page/remove-item-mutation'
import { useSaveStandingOrderMutation } from '../menu-page/save-standing-order-mutation'
import { useUpdateBasketMutation } from '../menu-page/update-basket-mutation'
import { useUpdateItemMutation } from '../menu-page/update-item-mutation'
import { useUpdateItemQtyMutation } from '../menu-page/update-item-qty-mutation'

type BasketErrorType = keyof typeof BasketErrors

type Props = {
  basketId: string | null
  standingOrderId: string | null
  language: Language
  deliveryFee?: number
  minOrderValueExTax: number | null
  onBasketDataChange: (newBasketData: BasketFragment) => void
}

export const useSelectedBasketState = ({
  basketId,
  standingOrderId,
  language,
  deliveryFee,
  minOrderValueExTax,
  onBasketDataChange,
}: Props) => {
  const [newItemCount, newItemCountState] = useCounter(0)
  const [basketError, setBasketError] = useState<{
    basketId: string
    error: BasketErrorType
  } | null>(null)

  const { setAddingItem, setUpdatingItem, addingItem } = useBasketStateStore()

  useEffect(() => {
    const { setAddingItem: resetAdding, setUpdatingItem: resetUpdating } =
      useBasketStateStore.getState()
    resetAdding(false)
    resetUpdating(false)
  }, [])

  const {
    authenticatedBaskets,
    unauthenticatedBaskets,
    deletedBaskets,
    config: selectionConfig,
  } = useBasketSelectionState()

  const validBasketIds = useMemo(() => {
    const baskets = selectionConfig.isAuthenticatedUser
      ? authenticatedBaskets
      : unauthenticatedBaskets
    return new Set(baskets.filter((b) => !deletedBaskets.includes(b.id)).map((b) => b.id))
  }, [
    authenticatedBaskets,
    unauthenticatedBaskets,
    deletedBaskets,
    selectionConfig.isAuthenticatedUser,
  ])

  const {
    run: runGetBasket,
    basket: fetchedBasket,
    loading: loadingBasket,
    called: getBasketCalled,
    error: getBasketError,
  } = useGetBasketQuery()
  const {
    run: runGetStandingOrder,
    standingOrder: fetchedStandingOrder,
    loading: loadingStandingOrder,
    called: getStandingOrderCalled,
    error: getStandingOrderError,
  } = useGetStandingOrderQuery()

  const updateBasketMutation = useUpdateBasketMutation()

  const isValidStandingOrder = fetchedStandingOrder && fetchedStandingOrder._id === standingOrderId
  const rawBasketData = isValidStandingOrder
    ? fetchedStandingOrder?.standingOrderTemplate
    : fetchedBasket

  const basket:
    | (BasketFragment & { standingOrderSettings?: GmMenuStandingOrderDataFragment })
    | null = useMemo(() => {
    const basketData =
      (rawBasketData && basketId === rawBasketData.id) || (rawBasketData && isValidStandingOrder)
        ? populateBasketTranslationFields(rawBasketData, language)
        : null
    if (!basketData) return null
    return Object.assign(basketData, { standingOrderSettings: fetchedStandingOrder ?? undefined })
  }, [basketId, rawBasketData, language, isValidStandingOrder, fetchedStandingOrder])

  const editSessionId = basket?.editSessionId

  const addItemMutation = useAddItemMutation(
    deliveryFee,
    Boolean(isValidStandingOrder),
    editSessionId,
  )
  const updateItemMutation = useUpdateItemMutation(
    deliveryFee,
    Boolean(isValidStandingOrder),
    editSessionId,
  )
  const updateItemQtyMutation = useUpdateItemQtyMutation(
    deliveryFee,
    Boolean(isValidStandingOrder),
    editSessionId,
  )
  const removeItemMutation = useRemoveItemMutation(
    deliveryFee,
    Boolean(isValidStandingOrder),
    editSessionId,
  )
  const saveStandingOrderMutation = useSaveStandingOrderMutation(
    basket?.id,
    editSessionId,
    standingOrderId,
  )

  const minOrderValue = minOrderValueExTax ?? 0
  const minimumOrderReached = (basket?.subtotalBreakdown.subtotalExTax ?? 0) >= minOrderValue
  const minSpendRemaining = basket
    ? minOrderValue - (basket.subtotalBreakdown.subtotalExTax ?? 0)
    : 0

  const addItem: AddItemToBasket = useCallback(
    (basket, payload) => {
      newItemCountState.inc()
      setTimeout(() => newItemCountState.dec(), 2000)
      setAddingItem(true) // Set shared loading state
      addItemMutation.run(basket, payload)
    },
    [addItemMutation, newItemCountState, setAddingItem],
  )

  useEffect(() => {
    setAddingItem(addItemMutation.loading)
  }, [addItemMutation.loading, setAddingItem])

  useEffect(() => {
    const isUpdating = [
      updateBasketMutation.loading,
      updateItemMutation.loading,
      updateItemQtyMutation.loading,
      removeItemMutation.loading,
      saveStandingOrderMutation.loading,
    ].some(Boolean)

    setUpdatingItem(isUpdating)
  }, [
    updateBasketMutation.loading,
    updateItemMutation.loading,
    updateItemQtyMutation.loading,
    removeItemMutation.loading,
    saveStandingOrderMutation.loading,
    setUpdatingItem,
  ])

  useEffect(() => {
    if (!basketId) return
    // Skip fetches for baskets the user has just deleted (the row will still
    // be selected for a tick while delete propagates). Otherwise fetch — note
    // that a freshly-created draft is selected before it appears in
    // validBasketIds, so we can't gate on that set alone.
    if (deletedBaskets.includes(basketId)) return
    setBasketError(null)
    void runGetBasket(basketId)
  }, [basketId, runGetBasket, deletedBaskets])

  useEffect(() => {
    if (!standingOrderId) return
    void runGetStandingOrder(standingOrderId)
  }, [standingOrderId, runGetStandingOrder])

  useEffect(() => {
    if (!basketId || basketError) return
    if (getBasketError) setBasketError({ basketId, error: 'COULD_NOT_FETCH_BASKET' })
    if (updateItemQtyMutation.error || saveStandingOrderMutation.error)
      setBasketError({ basketId, error: 'COULD_NOT_UPDATE_BASKET' })
    if (addItemMutation.error) setBasketError({ basketId, error: 'COULD_NOT_ADD_ITEM_TO_BASKET' })
    if (updateItemMutation.error)
      setBasketError({ basketId, error: 'COULD_NOT_UPDATE_ITEM_IN_BASKET' })
    if (updateItemQtyMutation.error)
      setBasketError({ basketId, error: 'COULD_NOT_UPDATE_ITEM_QTY_IN_BASKET' })
    if (removeItemMutation.error)
      setBasketError({ basketId, error: 'COULD_NOT_REMOVE_ITEM_FROM_BASKET' })
    if (getStandingOrderError) setBasketError({ basketId, error: 'COULD_NOT_FETCH_BASKET' })
  }, [
    getBasketError,
    getStandingOrderError,
    addItemMutation.error,
    updateItemMutation.error,
    updateItemQtyMutation.error,
    removeItemMutation.error,
    saveStandingOrderMutation.error,
    basketId,
    basketError,
  ])

  useEffect(() => {
    if (!basket) return
    onBasketDataChange(basket)
  }, [basket, onBasketDataChange])

  return {
    basket,
    basketError,
    minimumOrderReached,
    minSpendRemaining,
    newItemJustAdded: newItemCount > 0,
    addingItem,
    updating: [
      updateBasketMutation,
      addItemMutation,
      updateItemMutation,
      updateItemQtyMutation,
      removeItemMutation,
      saveStandingOrderMutation,
    ].some((r) => r.loading),
    standingOrderUpdated: saveStandingOrderMutation.updated,
    saveStandingOrderChanges: saveStandingOrderMutation.run,
    updateItemInBasket: updateItemMutation.run,
    updateBasket: updateBasketMutation.run,
    updateItemQty: updateItemQtyMutation.run,
    removeItemFromBasket: removeItemMutation.run,
    addItemToBasket: addItem,
    loading:
      loadingStandingOrder ||
      loadingBasket ||
      (!basketError && Boolean(basketId && basketId !== basket?.id)),
    called: getBasketCalled || getStandingOrderCalled,
  }
}
